import { useQuery } from '@tanstack/react-query'

import { estimateMiles } from '@src/api/miles.api'
import { IExchangeData } from '@src/types/exchange'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('useMilesEstimate')

interface UseMilesEstimateParams {
    sellData: IExchangeData
    buyData: IExchangeData
    walletAddress?: string
    enabled?: boolean
}

export const useMilesEstimate = ({ sellData, buyData, walletAddress, enabled = true }: UseMilesEstimateParams) => {
    // Only include the necessary fields in the query key to avoid unnecessary refetches
    const queryKey = [
        'milesEstimate',
        {
            sellChainId: sellData.chainId,
            sellTokenAddress: sellData.token.address,
            sellValue: sellData.value.toString(),
            buyChainId: buyData.chainId,
            buyTokenAddress: buyData.token.address,
            buyValue: buyData.value.toString(),
            walletAddress,
        },
    ]

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!walletAddress || isFeatureEnabled('USE_TESTNETS_ONLY')) {
                return { miles: 0, multiplier: 1 }
            }
            try {
                return await estimateMiles(sellData, buyData, walletAddress)
            } catch (error) {
                logger.error('Failed to estimate miles:', error)
                return { miles: 0, multiplier: 1 }
            }
        },
        // Only run the query if we have all required data and enabled is true
        enabled:
            enabled &&
            !!walletAddress &&
            !!sellData.token &&
            !!buyData.token &&
            !!sellData.value &&
            !!buyData.value &&
            parseFloat(sellData.value.toString()) > 0,
        // Cache the data for 1 minute
        staleTime: 60 * 1000,
        // Keep the cached data for 5 minutes
        gcTime: 5 * 60 * 1000,
    })
}
